import { useState, useEffect, useRef } from 'react';
import { X, Download, ZoomIn, ZoomOut, ExternalLink } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PdfViewerProps = {
  url: string;
  onClose: () => void;
  relatedLink?: { url: string; label: string };
};

// Hook to detect touch device
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      // Check if device supports touch and has coarse pointer (typical for mobile)
      return window.matchMedia('(pointer: coarse)').matches ||
        ('ontouchstart' in window) ||
        (navigator.maxTouchPoints > 0);
    };
    setIsMobile(checkMobile());
  }, []);
  return isMobile;
}

export function PdfViewer({ url, onClose, relatedLink }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const isMobile = useIsMobile();

  // Desktop state
  const [desktopScale, setDesktopScale] = useState<number>(1.2);

  // Mobile state (visual tracker only)
  const [mobileScale, setMobileScale] = useState<number>(0.4);
  const [isReady, setIsReady] = useState(false);

  // Need to import correct type if available, or use any
  const transformWrapperRef = useRef<any>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);

    // Center view immediately when loaded (only relevant for mobile wrapper)
    if (transformWrapperRef.current) {
      transformWrapperRef.current.centerView(0.4, 0); // Start zoomed out
    }

    // Fade in shortly after to prevent jumping visual (only visibly affects mobile)
    setTimeout(() => {
      setIsReady(true);
    }, 100);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = 'document.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render Header Controls
  const renderControls = (scale: number, onZoomIn: () => void, onZoomOut: () => void) => (
    <div className="absolute top-0 left-0 right-0 z-[110] bg-black/80 backdrop-blur-md border-b border-white/10">
      <div className="flex items-center justify-between p-4 px-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onClose}
            className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-all active:scale-95 z-[120]"
          >
            <X size={24} className="text-white" />
          </button>
          <div className="flex flex-col">
            <span className="text-white text-sm font-bold tracking-tight">PDF Document</span>
            <span className="text-white/50 text-[10px] uppercase font-black tracking-widest leading-none">
              {numPages} {numPages === 1 ? 'Page' : 'Pages'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-white/5 p-1 rounded-2xl border border-white/10">
          <button
            type="button"
            onClick={onZoomOut}
            className="p-2 rounded-xl hover:bg-white/10 text-white/70 hover:text-white transition-all disabled:opacity-30"
          >
            <ZoomOut size={20} />
          </button>
          <span className="text-white text-[13px] font-black w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={onZoomIn}
            className="p-2 rounded-xl hover:bg-white/10 text-white/70 hover:text-white transition-all disabled:opacity-30"
          >
            <ZoomIn size={20} />
          </button>

          {relatedLink && (
            <div className="flex items-center">
              <div className="w-px h-6 bg-white/10 mx-1" />
              <a
                href={relatedLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl hover:bg-white/10 text-blue-400 hover:text-blue-300 transition-all flex items-center gap-2 px-3 tracking-wide text-xs font-bold uppercase pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="hidden sm:inline">{relatedLink.label}</span>
                <ExternalLink size={18} />
              </a>
            </div>
          )}

          <div className="w-px h-6 bg-white/10 mx-1" />
          <button
            type="button"
            onClick={handleDownload}
            className="p-2 rounded-xl hover:bg-white/10 text-white/70 hover:text-white transition-all"
          >
            <Download size={20} />
          </button>
        </div>
      </div>
    </div>
  );

  const renderLoader = () => (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-6 pointer-events-none">
      <div className="w-16 h-16 border-4 border-white/5 border-t-blue-500 rounded-full animate-spin" />
      <div className="text-white/40 text-[11px] font-black uppercase tracking-[0.3em] animate-pulse">
        Loading Document...
      </div>
    </div>
  );

  const commonPageProps = {
    renderTextLayer: true,
    renderAnnotationLayer: false,
    className: "bg-white shadow-2xl",
    loading: <div className="w-[300px] h-[400px] bg-white/5 text-white/20 flex items-center justify-center">Loading...</div>
  };

  if (isMobile) {
    // === MOBILE VIEW (Pinch & Pan enabled) ===
    return (
      <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col icon-picker-modal">
        <TransformWrapper
          initialScale={0.4}
          minScale={0.2}
          maxScale={4}
          centerOnInit
          limitToBounds={true}
          ref={transformWrapperRef}
          onTransformed={(e) => setMobileScale(e.state.scale)}
        >
          {({ zoomIn, zoomOut }) => (
            <>
              {renderControls(mobileScale, () => zoomIn(), () => zoomOut())}

              <div className="flex-1 w-full h-full overflow-hidden flex flex-col pt-20">
                <TransformComponent
                  wrapperClass="!w-full !h-full"
                  contentClass="flex flex-col items-center gap-8 py-8 px-4 min-h-full"
                >
                  <div style={{ opacity: isReady ? 1 : 0, transition: 'opacity 0.3s ease-in' }}>
                    <Document
                      file={url}
                      onLoadSuccess={onDocumentLoadSuccess}
                      loading={renderLoader()}
                      className="flex flex-col items-center gap-8"
                    >
                      {Array.from({ length: numPages }, (_, i) => (
                        <div key={i + 1}>
                          <Page
                            pageNumber={i + 1}
                            scale={1.2} // High quality base
                            {...commonPageProps}
                          />
                        </div>
                      ))}
                    </Document>
                  </div>
                </TransformComponent>
              </div>
            </>
          )}
        </TransformWrapper>
        {/* Style for Text Selection on Mobile if possible or generally */}
        <style>{`
          .react-pdf__Page__textContent {
             user-select: text !important;
             -webkit-user-select: text !important;
             pointer-events: auto !important;
          }
        `}</style>
      </div>
    );
  }

  // === DESKTOP VIEW (Native Scroll & Selection) ===
  const handleDesktopZoomIn = () => setDesktopScale(s => Math.min(s + 0.25, 3));
  const handleDesktopZoomOut = () => setDesktopScale(s => Math.max(s - 0.25, 0.5));

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col">
      {renderControls(desktopScale, handleDesktopZoomIn, handleDesktopZoomOut)}

      <div className="flex-1 overflow-y-auto pt-24 pb-20 px-8 scroll-smooth">
        <div className="flex flex-col items-center min-h-full">
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={renderLoader()}
            className="flex flex-col items-center gap-8"
          >
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i + 1}>
                <Page
                  pageNumber={i + 1}
                  scale={desktopScale}
                  {...commonPageProps}
                />
              </div>
            ))}
          </Document>
        </div>
      </div>

      <style>{`
        /* Ensure text selection works */
        .react-pdf__Page__textContent {
            user-select: text;
            cursor: text;
        }
        ::selection {
            background: rgba(0, 100, 255, 0.3);
        }
      `}</style>
    </div>
  );
}
